
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import TailwindLayout from 'TailwindcssComponents/TailwindLayout';
import TopBar from 'TailwindcssComponents/TopBar';
import DataTable from 'TailwindcssComponents/Tables';
import axios from 'axios';
import FooterTailwind from 'TailwindcssComponents/Footer';
import DeleteDialog from 'components/DeleteDialogBox/deleteDialog';
import { ToastContainer, toast } from 'react-toastify';
import { UserDataContext } from 'context';
import defaultLogo from 'assets/images/logos/defaultlogo.png';

export default function EventOrganizations() {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const { userData, selectedOrganization } = useContext(UserDataContext);
    const userRole = userData?.isAdmin ? 'admin' : selectedOrganization?.name || 'user'; const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [organizationsData, setOrganizationsData] = useState([]);
    const [openDeleteDialogBox, setOpenDeleteDialogBox] = useState();
    const [selectedOrgId, setSelectedOrgId] = useState(null);
    const [totalItemsCount, setTotalItemsCount] = useState(0);
    const [totalPagesCount, setTotalPagesCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [orgStatus, setOrgStatus] = useState(null);
    const [eventCategories, setEventCategories] = useState(null);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [selectedCategoryName, setSelectedCategoryName] = useState();
    const [selectedDate, setSelectedDate] = useState(null);
    const [filteredOrganizations, setFilteredOrganizations] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isArchived, setIsArchived] = useState(false);

    const fetchEventOrganizations = async () => {
        const params = {
            page: currentPage,
            limit: rowsPerPage,
            name: searchTerm,
            ...(isArchived !== null ? { isArchived } : {})
        };
        try {
            setIsLoading(true);
            const response = await axios.get(`${apiUrl}auth-service/org`,
                {
                    params,
                    headers: { 'Authorization': `Bearer ${bearerToken}` }
                }
            );
            if (response.data?.success) {
                setOrganizationsData(response.data.data);
                setFilteredOrganizations(response.data.data);
                setTotalItemsCount(response.data.totalItems || 0);
                setTotalPagesCount(response.data.totalPages || 0);
            } else throw new Error(response.data?.message || 'Error in fetching organizations');
        } catch (error) {
            console.error(error.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${apiUrl}event-service/event-category`);
            if (response.data?.success) {
                setEventCategories(response.data.data);
            } else throw new Error(response.data?.message || 'Error in fetching category');
        } catch (error) {
            console.error(error.response?.data?.message || error.message);
        }
    };

    const handleActionClick = (id, action) => {
        const actions = {
            view: () => navigate(`/eventOrganizations/single-eventOrganization/${id}`),
            edit: () => navigate(`/eventOrganizations/edit-eventOrganization/${id}`),
            delete: () => handleDelete(id),
            archive: () => handleArchive(id, true),
            unarchive: () => handleArchive(id, false),
        };
        actions[action]?.();
    };

    const handleArchive = async (id, isArchived) => {
        try {
            const url = `${apiUrl}auth-service/org/${isArchived ? 'archive' : 'unarchive'}/${id}`;

            const response = await axios.patch(url, {}, {
                headers: { 'Authorization': `Bearer ${bearerToken}` }
            });

            if (response?.data.success) {
                toast.warning(response.data.message || `Organization has been ${isArchived ? 'archived' : 'unarchived'}`);
                fetchEventOrganizations();
            } else {
                throw new Error(response?.data.message || 'Unable to update');
            }
        } catch (error) {
            const errorMsg = error.response?.data.message || error.message;
            console.error(errorMsg);
            toast.error(errorMsg);
        }
    };


    const handleDelete = async (id) => {
        setSelectedOrgId(id);
        setOpenDeleteDialogBox(true);
    };

    const closeDeleteDialogBox = () => {
        setOpenDeleteDialogBox(false);
    };

    const handleDeleteConfirm = async () => {
        try {
            setIsLoading(true);
            const response = await axios.delete(`${apiUrl}auth-service/org/${selectedOrgId}`,
                {
                    headers: { 'Authorization': `Bearer ${bearerToken}` }
                });
            if (response?.data.success) {
                toast.warning(response?.data.message || 'Event has been deleted');
            }
            else throw new Error(response?.data.message || 'Unable to delete');
        } catch (error) {
            console.error(error.response?.data.message || error.message);
            toast.error(error.response?.data.message || error.message);
        } finally {
            fetchEventOrganizations();
            setOpenDeleteDialogBox(false);
            setIsLoading(false);
        }
    };

    const filterOrganizations = (term) => {
        if (!term) {
            setFilteredOrganizations(organizationsData); // Reset to all data if search term is empty
            return;
        }
        const lowerCaseTerm = term.toLowerCase();
        const filtered = organizationsData.filter((org) =>
            org.name.toLowerCase().includes(lowerCaseTerm) || // Filter by name
            org.description?.toLowerCase().includes(lowerCaseTerm) // Optional: Filter by description
        );
        setFilteredOrganizations(filtered);
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
        filterOrganizations(term);
    };

    useEffect(() => {
        fetchEventOrganizations();
        if (selectedCategoryId && eventCategories) {
            const selectedCategory = eventCategories?.find((category) => category.id === selectedCategoryId);
            setSelectedCategoryName(selectedCategory ? selectedCategory.name : null);
        }
    }, [currentPage, rowsPerPage, orgStatus, selectedCategoryId, selectedDate, searchTerm, bearerToken, isArchived]);

    const formattedDate = (date) => {
        const options = { day: '2-digit', month: 'short', year: 'numeric' };
        return new Date(date).toLocaleDateString('en-GB', options);
    };

    const columns = [
        {
            key: 'name', label: 'Organization Name',
            render: (row) => (
                <div className="flex items-center gap-3">
                    <img
                        src={row.orgImage?.secure_url || defaultLogo}
                        alt={`${row.name} logo`}
                        className="w-10 h-10 rounded-full object-cover"
                    />
                    <span>{row.name}</span>
                </div>
            ),
        },
        { key: 'email', label: 'Email', render: (row) => (row.email) },
        { key: 'phone', label: 'Phone', render: (row) => (row.phone) },
        {
            key: 'status',
            label: 'Status',
            render: (row) => (
                <div
                    className={`border rounded ${row.isDelete ? 'bg-red-100 text-red-700 border-red-500' : 'bg-[#45B369] bg-opacity-15 text-[#45B369] border-[#45B369]'} w-fit h-7 flex items-center`}
                >
                    <div className="py-1 px-3">{row.isDelete ? 'Inactive' : 'Active'}</div>
                </div>
            ),
        },
    ];

    const getStatusColor = (status) => {
        const statusColors = {
            active: 'bg-[#45B369] bg-opacity-15 text-[#45B369] border-[#45B369]',
            published: 'bg-[#F86624] bg-opacity-15 text-[#EF4A00] border-[#EF4A00]',
            draft: 'bg-red-100 text-red-700 border-red-500',
        };
        return statusColors[status] || 'bg-gray-100 text-gray-700';
    };

    return (
        <TailwindLayout>
            <TopBar />
            <div className="p-12 bg-gray-100 min-h-screen">
                <DataTable
                    title={'Event Organizations'}
                    rows={filteredOrganizations}
                    columns={columns}
                    onActionClick={handleActionClick}
                    options={{ showHeader: true, showHeaderTwo: false, viewOption: true, editOption: true, addoption: false, statusFilterOption: false, dateFilterOption: false, typeFilterOption: false, searchOption: true, archiveFilterOption: true }}
                    totalCount={totalItemsCount}
                    pagesCount={totalPagesCount}
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(limit) => setRowsPerPage(limit)}
                    onPageChange={(page) => setCurrentPage(page)}
                    orgStatus={orgStatus}
                    onStatusChange={(status) => setOrgStatus(status)}
                    categories={eventCategories}
                    onCategoryChange={(id) => setSelectedCategoryId(id)}
                    category={selectedCategoryName}
                    onDateChange={(date) => setSelectedDate(date)}
                    filteredDate={selectedDate}
                    onSearch={handleSearch}
                    searcItem={searchTerm}
                    role={userRole}
                    isArchivedStatus={isArchived}
                    onToggle={(value) => setIsArchived(value)}
                    loaderStatus={isLoading}
                    addButtonOnlyOption={true}
                    addButtonTitle={"Add Organization"}
                    handleDialogBoxes={() => navigate("/add-eventOrganization")}

                />
            </div>
            <DeleteDialog
                open={openDeleteDialogBox}
                onClose={closeDeleteDialogBox}
                onDelete={handleDeleteConfirm}
                name={'organization'}
            />
            <ToastContainer
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            <FooterTailwind />
        </TailwindLayout>
    )
}